/**
 * Wildebeast premium product gallery
 * ---------------------------------------------------------------------------
 * The swipe gesture itself is native CSS scroll-snap — this only adds the
 * things CSS can't do: thumbnail sync, arrows, keyboard control, variant
 * image jumping, and the zoom lightbox.
 */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

class WbGallery extends HTMLElement {
  connectedCallback() {
    this.stage = this.querySelector('[data-gal-stage]');
    if (!this.stage) return;

    this.slides = Array.from(this.querySelectorAll('[data-gal-slide]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-gal-thumb]'));
    this.prevBtn = this.querySelector('[data-gal-prev]');
    this.nextBtn = this.querySelector('[data-gal-next]');
    this.counter = this.querySelector('[data-gal-current]');

    this.loop = this.dataset.loop === 'true';
    this.index = 0;

    this.setupObserver();
    this.setupThumbs();
    this.setupArrows();
    this.setupKeyboard();
    this.setupVariantSync();
    this.setupZoom();

    this.updateUi();
  }

  /**
   * Tracks the visible slide by observing intersection rather than listening
   * to scroll — accurate during momentum scrolling, and far cheaper.
   */
  setupObserver() {
    if (!('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const index = Number(entry.target.dataset.index);
          if (Number.isNaN(index) || index === this.index) return;
          this.index = index;
          this.updateUi();
        });
      },
      { root: this.stage, threshold: 0.6 }
    );

    this.slides.forEach((slide) => this.observer.observe(slide));
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  setupThumbs() {
    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.index)));
    });
  }

  setupArrows() {
    this.prevBtn?.addEventListener('click', () => this.goTo(this.index - 1));
    this.nextBtn?.addEventListener('click', () => this.goTo(this.index + 1));
  }

  setupKeyboard() {
    this.stage.setAttribute('tabindex', '0');
    this.stage.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goTo(this.index + 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goTo(this.index - 1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.goTo(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        this.goTo(this.slides.length - 1);
      }
    });
  }

  /**
   * Jumps to the selected variant's image. Listens to both the theme's own
   * variant event and raw form changes, because different Horizon versions
   * (and third-party apps) emit different things.
   */
  setupVariantSync() {
    const jumpToMedia = (mediaId) => {
      if (!mediaId) return;
      const target = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (target >= 0) this.goTo(target);
    };

    document.addEventListener('variant:update', (event) => {
      jumpToMedia(event.detail?.resource?.featured_media?.id ?? event.detail?.variant?.featured_media?.id);
    });

    document.addEventListener('variant:change', (event) => {
      jumpToMedia(event.detail?.variant?.featured_media?.id);
    });

    // Fallback: watch the variant id input inside the surrounding product form.
    const form = this.closest('form, product-form-component, .product-information, [data-product-form]')
      || document.querySelector('form[action*="/cart/add"]');

    form?.addEventListener('change', async (event) => {
      if (!event.target.matches('input[name="id"], select[name="id"]')) return;
      const variantId = event.target.value;
      if (!variantId) return;

      try {
        const handle = this.dataset.productHandle || window.location.pathname.split('/products/')[1]?.split('?')[0];
        if (!handle) return;
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.js`);
        if (!response.ok) return;
        const product = await response.json();
        const variant = product.variants.find((v) => String(v.id) === String(variantId));
        jumpToMedia(variant?.featured_media?.id);
      } catch {
        /* Non-critical — the gallery just doesn't auto-jump. */
      }
    });
  }

  goTo(index) {
    const last = this.slides.length - 1;

    if (index < 0) index = this.loop ? last : 0;
    else if (index > last) index = this.loop ? 0 : last;

    const slide = this.slides[index];
    if (!slide) return;

    this.stage.scrollTo({
      left: slide.offsetLeft - this.stage.offsetLeft,
      behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth',
    });

    this.index = index;
    this.updateUi();
  }

  updateUi() {
    this.thumbs.forEach((thumb, i) => {
      const active = i === this.index;
      thumb.classList.toggle('wb-gal__thumb--active', active);
      thumb.setAttribute('aria-current', String(active));
      if (active) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });

    this.slides.forEach((slide, i) => {
      // Hide off-screen slides from assistive tech so the gallery reads as
      // one image, not a wall of them.
      if (i === this.index) slide.removeAttribute('aria-hidden');
      else slide.setAttribute('aria-hidden', 'true');
    });

    if (this.counter) this.counter.textContent = String(this.index + 1);

    if (!this.loop) {
      if (this.prevBtn) this.prevBtn.disabled = this.index === 0;
      if (this.nextBtn) this.nextBtn.disabled = this.index === this.slides.length - 1;
    }
  }

  /* ---- Zoom lightbox ---------------------------------------------------- */

  setupZoom() {
    if (this.dataset.zoom !== 'true') return;

    // The lightbox is emitted by the same block or section, but not always as
    // a direct sibling: the premium gallery block renders it next to this
    // element, while wb-product-main renders it at the section level. Walk
    // outward — parent first, then the enclosing section, then the document —
    // so both layouts find it.
    this.lightbox = this.parentElement?.querySelector('[data-gal-lightbox]')
      || this.closest('section')?.querySelector('[data-gal-lightbox]')
      || document.querySelector('[data-gal-lightbox]');
    if (!this.lightbox) return;

    this.lightboxImg = this.lightbox.querySelector('[data-gal-lightbox-img]');
    this.canvas = this.lightbox.querySelector('[data-gal-lightbox-canvas]');

    this.querySelectorAll('[data-gal-zoom]').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const img = trigger.querySelector('img');
        this.openZoom(trigger.dataset.full, img?.alt || '');
      });
    });

    this.lightbox.querySelector('[data-gal-lightbox-close]')?.addEventListener('click', () => this.closeZoom());

    // Pointer capture retargets the release click to the canvas even when the
    // press began on the image or ended after a pan, so event.target can't be
    // trusted here. Close only for a genuine backdrop tap: the press started
    // on the empty canvas itself and never travelled.
    this.canvas?.addEventListener('click', () => {
      if (this._pressOnBackdrop && !this._pressMoved) this.closeZoom();
    });

    this.onZoomKey = (event) => {
      if (event.key === 'Escape') this.closeZoom();
    };

    this.setupPanAndZoom();
  }

  openZoom(src, alt) {
    if (!src || !this.lightbox) return;

    this.lightboxImg.src = src;
    this.lightboxImg.alt = alt;

    // Belt and braces: guarantee the image opens fitted to the screen even if
    // the stylesheet is stale or missing on this page — inline styles cannot
    // be cached out or overridden. Without this, the raw 2400px image renders
    // at natural size and the lightbox opens "pre-zoomed".
    Object.assign(this.lightboxImg.style, {
      maxWidth: '100%',
      maxHeight: '100%',
      width: 'auto',
      height: 'auto',
      objectFit: 'contain',
    });

    this.resetTransform();

    this.lightbox.hidden = false;
    // Next frame, so the opacity transition actually runs.
    requestAnimationFrame(() => {
      this.lightbox.dataset.open = 'true';
    });

    document.documentElement.classList.add('wb-scroll-locked');
    document.addEventListener('keydown', this.onZoomKey);
    this.lightbox.querySelector('[data-gal-lightbox-close]')?.focus();
  }

  closeZoom() {
    if (!this.lightbox || this.lightbox.hidden) return;

    this.lightbox.dataset.open = 'false';
    document.documentElement.classList.remove('wb-scroll-locked');
    document.removeEventListener('keydown', this.onZoomKey);

    const hide = () => {
      this.lightbox.hidden = true;
      this.lightboxImg.src = '';
    };
    if (REDUCED_MOTION.matches) hide();
    else setTimeout(hide, 260);

    this.slides[this.index]?.querySelector('[data-gal-zoom]')?.focus();
  }

  resetTransform() {
    this.scale = 1;
    this.offsetX = 0;
    this.offsetY = 0;
    this.applyTransform();
  }

  applyTransform() {
    if (!this.lightboxImg) return;
    this.lightboxImg.style.translate = `${this.offsetX}px ${this.offsetY}px`;
    this.lightboxImg.style.scale = String(this.scale);
  }

  setupPanAndZoom() {
    if (!this.canvas) return;

    this.scale = 1;
    this.offsetX = 0;
    this.offsetY = 0;

    let dragging = false;
    let startX = 0;
    let startY = 0;
    let pressX = 0;
    let pressY = 0;

    // Wheel / trackpad zoom, clamped so the image can't be lost off-screen.
    // Instant — no transition. Animating every wheel tick makes the zoom lag
    // behind the gesture and feel rubbery; the 200ms ease belongs only to the
    // double-click toggle, applied via data-animate.
    this.canvas.addEventListener(
      'wheel',
      (event) => {
        event.preventDefault();
        delete this.lightboxImg.dataset.animate;
        const next = this.scale - event.deltaY * 0.002;
        this.scale = Math.min(4, Math.max(1, next));
        if (this.scale === 1) {
          this.offsetX = 0;
          this.offsetY = 0;
        }
        this.applyTransform();
      },
      { passive: false }
    );

    // Double-click / double-tap toggles between fit and 2× — the one place
    // the scale animation is wanted.
    this.canvas.addEventListener('dblclick', () => {
      this.lightboxImg.dataset.animate = 'true';
      this.scale = this.scale > 1 ? 1 : 2;
      if (this.scale === 1) {
        this.offsetX = 0;
        this.offsetY = 0;
      }
      this.applyTransform();
      setTimeout(() => delete this.lightboxImg.dataset.animate, 240);
    });

    const start = (x, y) => {
      dragging = true;
      startX = x - this.offsetX;
      startY = y - this.offsetY;
    };

    const move = (x, y) => {
      if (!dragging || this.scale === 1) return;
      this.offsetX = x - startX;
      this.offsetY = y - startY;
      this.applyTransform();
    };

    const end = () => {
      dragging = false;
    };

    this.canvas.addEventListener('pointerdown', (e) => {
      // Remember where and on what the press began — the click handler needs
      // this because pointer capture retargets the eventual click to the
      // canvas regardless of the true press target.
      this._pressOnBackdrop = e.target === this.canvas;
      this._pressMoved = false;
      pressX = e.clientX;
      pressY = e.clientY;
      delete this.lightboxImg.dataset.animate;
      this.canvas.setPointerCapture(e.pointerId);
      start(e.clientX, e.clientY);
    });
    this.canvas.addEventListener('pointermove', (e) => {
      if (dragging && Math.hypot(e.clientX - pressX, e.clientY - pressY) > 4) {
        this._pressMoved = true;
      }
      move(e.clientX, e.clientY);
    });
    this.canvas.addEventListener('pointerup', end);
    this.canvas.addEventListener('pointercancel', end);

    // Pinch to zoom on touch.
    let pinchStart = null;
    this.canvas.addEventListener(
      'touchstart',
      (event) => {
        if (event.touches.length !== 2) return;
        const [a, b] = event.touches;
        pinchStart = { dist: Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY), scale: this.scale };
      },
      { passive: true }
    );

    this.canvas.addEventListener(
      'touchmove',
      (event) => {
        if (event.touches.length !== 2 || !pinchStart) return;
        event.preventDefault();
        delete this.lightboxImg.dataset.animate;
        const [a, b] = event.touches;
        const dist = Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
        this.scale = Math.min(4, Math.max(1, pinchStart.scale * (dist / pinchStart.dist)));
        this.applyTransform();
      },
      { passive: false }
    );

    this.canvas.addEventListener('touchend', () => {
      pinchStart = null;
    });
  }
}

if (!customElements.get('wb-gallery')) customElements.define('wb-gallery', WbGallery);
